import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";

const clamp = (n, min, max) => Math.max(min, Math.min(n, max));

const usePreloader = () => {
  const loaded = useRef(new Set());
  const preload = (url) => {
    if (!url || loaded.current.has(url)) return;
    const img = new Image();
    img.decoding = "async";
    img.loading = "eager";
    img.src = url;
    img.onload = () => loaded.current.add(url);
    img.onerror = () => {};
  };
  const isLoaded = (url) => loaded.current.has(url);
  return { preload, isLoaded };
};

const ImageCarousel = ({
  images = [],
  interval = 6000,
  rounded = true,
  aspectRatio = "16/9",
  onIndexChange,
  initialIndex = 0,
  autoplay = false,
  wrap = true,
}) => {
  const total = images?.length ?? 0;

  const safeInitial = useMemo(
    () => (total > 0 ? clamp(initialIndex, 0, total - 1) : 0),
    [initialIndex, total]
  );

  const [current, setCurrent] = useState(safeInitial);

  useEffect(() => setCurrent(safeInitial), [safeInitial]);

  const onIndexChangeRef = useRef(onIndexChange);
  useEffect(() => {
    onIndexChangeRef.current = onIndexChange;
  }, [onIndexChange]);
  useEffect(() => {
    if (typeof onIndexChangeRef.current === "function" && total > 0) {
      onIndexChangeRef.current(current);
    }
  }, [current, total]);

  const { preload } = usePreloader();
  useEffect(() => {
    if (total <= 1) return;
    const next = (current + 1) % total;
    const prev = (current - 1 + total) % total;
    preload(images[next]);
    preload(images[prev]);
  }, [current, total, images, preload]);

  // autoplay
  useEffect(() => {
    if (!autoplay || total <= 1) return;
    const id = setInterval(() => {
      setCurrent((p) => (wrap ? (p + 1) % total : Math.min(p + 1, total - 1)));
    }, interval);
    return () => clearInterval(id);
  }, [autoplay, total, interval, wrap]);

  const nextSlide = () => {
    if (!total) return;
    setCurrent((p) => (wrap ? (p + 1) % total : Math.min(p + 1, total - 1)));
  };
  const prevSlide = () => {
    if (!total) return;
    setCurrent((p) => (wrap ? (p - 1 + total) % total : Math.max(p - 1, 0)));
  };

  if (total === 0) {
    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio,
          borderRadius: rounded ? 3 : 0,
          bgcolor: "action.hover",
        }}
      />
    );
  }

  const src = images[current];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        borderRadius: rounded ? 3 : 0,
        aspectRatio,
        "& img": { willChange: "opacity" },
      }}
    >
      <AnimatePresence initial={false} mode="wait">
        <motion.img
          key={src}
          src={src}
          alt={`slide-${current + 1}-of-${total}`}
          decoding="async"
          fetchpriority="high"
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            borderRadius: rounded ? 12 : 0,
            transform: "translateZ(0)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: "easeInOut" }}
        />
      </AnimatePresence>

      {total > 1 && (
        <>
          <IconButton
            onClick={prevSlide}
            sx={{
              position: "absolute",
              top: "50%",
              left: 16,
              transform: "translateY(-50%)",
              color: "#fff",
              backgroundColor: "rgba(0,0,0,0.3)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.45)" },
              backdropFilter: "blur(2px)",
            }}
            aria-label="Previous slide"
          >
            <ArrowBackIos fontSize="small" />
          </IconButton>

          <IconButton
            onClick={nextSlide}
            sx={{
              position: "absolute",
              top: "50%",
              right: 16,
              transform: "translateY(-50%)",
              color: "#fff",
              backgroundColor: "rgba(0,0,0,0.3)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.45)" },
              backdropFilter: "blur(2px)",
            }}
            aria-label="Next slide"
          >
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default ImageCarousel;
